<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Interpreter_Framework/Understanding_the_Framework';
    let nextLink= '/Documentation/Customizations/Editor_Customization';
    
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="customization-1"
  bind:intersecting={visibleById["customization-1"]}
  onIntersectChange={(visible) => setVisible("customization-1", visible)}
>Customization</SectionComponent>
<p>From the five parts of a language definition, Freon generates a complete work environment containing an editor,
scope provider (scoper), type provider (typer), validator, standard library, and utilities such as a parser and deparser.
These individual components are collectively referred to as <strong><em>fre-tools</em></strong>.
Other sections of this documentation describe how to fine-tune the generated fre-tools using the files in the <a
  href="/Documentation/Overview/Getting_Started#creating-a-project-2"
><em>defs</em> folder</a>
(i.e. the folder containing your definition files).
(See for instance <a
  href="/Documentation/Defining_an_Editor"
>Defining_an_editor</a>.)
In this section, we focus on deeper customizations implemented through TypeScript code.</p>
<SectionComponent tag="h2" 
  id="stacked-architecture-2"
  bind:intersecting={visibleById["stacked-architecture-2"]}
  onIntersectChange={(visible) => setVisible("stacked-architecture-2", visible)}
>Stacked Architecture</SectionComponent>
<p>Customization is made possible by Freon’s <em>stacked architecture</em>.
This architecture makes it possible to create your language,
and customize it step-by-step in an agile fashion.
Freon’s design philosophy can be summarized by the following quote from
the well-known graphical user interface designer Alan Kay:</p>
<blockquote>
<p><em>Simple things should be simple, complex things should be possible.</em>
(<a href="https://en.wikipedia.org/wiki/Alan_Kay" target="_blank">Alan Kay, Turing Award Winner</a>)</p>
</blockquote>
<p>(Read our <a href="/Background">Background</a> to understand why we have chosen this architecture.)
Freon supports three levels of customization.</p>
<ol>
<li>The default level, which is always available.</li>
<li>The definition level, where the fre-tools are being generated based on the input from the definition files (e.g. a <code>.scope</code> file).</li>
<li>The customization level, where handwritten TypeScript code can be added or used to replace parts of the generated output.</li>
</ol>
<p>Freon aims to provide around 80% of the required functionality of the work environment through the first two levels.</p>
<Figure 
imageName={'documentation/layered-architecture2.png'} 
caption={'The Stacked Architecture'}
figureNumber={1}
/>
<p>Freon combines the definitions for each tool, together with the customized TypeScript code into one application,
where the third level precedes the second, and the second level precedes the first.
For example, the generated editor determines for each <a
  href="/Documentation/Creating_the_Metamodel/Language_Structure#concept-4"
><strong>concept</strong></a> in the language which projection to use, in the following order:</p>
<ol>
<li>use the <em>hand-written projection</em> from the Customization level, when this is present. If not, the editor will</li>
<li>use the <em>projection generated from the .edit definition</em> from the Freon definition Level, when this definition is present.
Finally, when no definition is present, the editor will</li>
<li>fall back to the <em>default projection</em>, the one generated when no <code>.edit</code> definition file is present.</li>
</ol>
<Figure
imageName={'documentation/fall-through.png'}
caption={'Projection Lookup for an AST Node'}
figureNumber={2}
/>
<p>This layered approach allows language engineers to start quickly with a working environment and
refine it gradually at either the definition or customization level.
For this purpose,
the <a
  href="/Documentation/Overview/Getting_Started#the-command-line-interface-9"
>Command Line Interface</a> includes separate commands
for generating the different fre-tools.</p>
<SectionComponent tag="h2" 
  id="customizability-of-the-fre-tools-3"
  bind:intersecting={visibleById["customizability-of-the-fre-tools-3"]}
  onIntersectChange={(visible) => setVisible("customizability-of-the-fre-tools-3", visible)}
>Customizability of the Fre-Tools</SectionComponent>
<p>This table gives an overview of the fre-tools, and shows how you can adjust them to your needs.</p>
<table>
<thead>
<tr>
<th align="left">Workbench Component</th>
<th align="center">Has Default</th>
<th align="center">Level 2 Definition</th>
<th align="center">Level 3 Definition</th>
</tr>
</thead>
<tbody>
<tr>
<td align="left">language structure</td>
<td align="center">no</td>
<td align="center">.ast</td>
<td align="center">no</td>
</tr>
<tr>
<td align="left">projectional editor</td>
<td align="center">yes</td>
<td align="center">.edit</td>
<td align="center">yes</td>
</tr>
<tr>
<td align="left">scope provider</td>
<td align="center">yes</td>
<td align="center">.scope</td>
<td align="center">yes</td>
</tr>
<tr>
<td align="left">validator</td>
<td align="center">yes</td>
<td align="center">.valid</td>
<td align="center">yes</td>
</tr>
<tr>
<td align="left">type provider</td>
<td align="center">yes</td>
<td align="center">.type</td>
<td align="center">yes</td>
</tr>
<tr>
<td align="left">interpreter</td>
<td align="center">no</td>
<td align="center">none available yet</td>
<td align="center">yes</td>
</tr>
<tr>
<td align="left">standard library</td>
<td align="center">yes</td>
<td align="center">.ast</td>
<td align="center">not yet</td>
</tr>
<tr>
<td align="left">parser</td>
<td align="center">yes</td>
<td align="center">.edit</td>
<td align="center">yes (using <a href="https://github.com/dhakehurst/net.akehurst.language" target="_blank">AGL</a>)</td>
</tr>
<tr>
<td align="left">unparser</td>
<td align="center">yes</td>
<td align="center">.edit</td>
<td align="center">not yet</td>
</tr>
<tr>
<td align="left">json exporter/importer</td>
<td align="center">yes</td>
<td align="center">.ast</td>
<td align="center">no</td>
</tr>
<tr>
<td align="left">visitor pattern implementation</td>
<td align="center">yes</td>
<td align="center">.ast</td>
<td align="center">can be extended</td>
</tr>
<tr>
<td align="left">web app</td>
<td align="center">yes</td>
<td align="center">none available</td>
<td align="center">can be changed or replaced</td>
</tr>
</tbody>
</table>
<p>If you are missing a specific tool or feature, please contact us at <a
  href="mailto:info@openmodeling.nl"
>info@openmodeling.nl</a>.</p>
<SectionComponent tag="h2" 
  id="adding-a-fre-tool-4"
  bind:intersecting={visibleById["adding-a-fre-tool-4"]}
  onIntersectChange={(visible) => setVisible("adding-a-fre-tool-4", visible)}
>Adding A Fre-tool</SectionComponent>
<p>Freon automatically generates TypeScript templates to support your customizations. You can find these
in the files <code>~/freon/editor/CustomYourLanguageNameProjection.ts</code>, <code>~/freon/editor/CustomYourLanguageNameActions.ts</code>, etc.
(where <code>YourLanguageName</code> is a placeholder for the name of the language as defined in your .ast file). You can use
these files to add your own special elements.</p>
<p>The custom files can be renamed and/or
place them in another location, or you can create your own set of custom projection files.
In that case, you must update the configuration file <code>~/freon/config/FreonConfiguration</code>.</p>
<p>The following is an example of a generated configuration file that registers custom projections, actions, and other extensions.</p>
<pre class="language-ts"><code class="language-ts">// Insurance/src/freon/config/FreonConfiguration.ts

// Generated by the Freon Language Generator.
// TEMPLATE: ConfigurationTemplate.generate(...)

import &#123; type FreProjection, type FreCombinedActions, type FreTyper, type FreStdlib, type FreScoper &#125; from "@freon4dsl/core";

import &#123; CustomInsuranceModelActions, CustomInsuranceModelProjection &#125; from "../editor/index.js";
import &#123; CustomInsuranceModelScoper &#125; from "../scoper/index.js";
import &#123; CustomInsuranceModelTyperPart &#125; from "../typer/CustomInsuranceModelTyperPart.js";
import &#123; CustomInsuranceModelValidator &#125; from "../validator/index.js";
import &#123; CustomInsuranceModelStdlib &#125; from "../stdlib/CustomInsuranceModelStdlib.js";
import &#123; type InsuranceModelCheckerInterface &#125; from "../validator/gen/index.js";

/**
 * Class FreonConfiguration is the place where you can add all your customisations.
 * These will be used through the 'freonConfiguration' constant by any generated
 * part of your language environment.
 */
class FreonConfiguration &#123;
    // add your custom editor projections here
    customProjection: FreProjection[] = [new CustomInsuranceModelProjection()];
    // add your custom editor actions here
    customActions: FreCombinedActions[] = [new CustomInsuranceModelActions()];
    // add your custom validations here
    customValidations: InsuranceModelCheckerInterface[] = [new CustomInsuranceModelValidator()];
    // add your custom scopers here
    customScopers: FreScoper[] = [new CustomInsuranceModelScoper()];
    // add your custom type-providers here
    customTypers: FreTyper[] = [new CustomInsuranceModelTyperPart()];
    // add extra predefined instances here
    customStdLibs: FreStdlib[] = [new CustomInsuranceModelStdlib()];
&#125;

export const freonConfiguration = new FreonConfiguration();
</code></pre>
<SectionComponent tag="h2" 
  id="replacing-a-fre-tool-5"
  bind:intersecting={visibleById["replacing-a-fre-tool-5"]}
  onIntersectChange={(visible) => setVisible("replacing-a-fre-tool-5", visible)}
>Replacing a Fre-tool</SectionComponent>
<p>All fre-tools can be fully replaced at the third level, overriding both the default and definition levels.
This is done by implementing their respective <a
  href="/Documentation/Under_the_Hood/FreTool_Interfaces"
>interfaces</a>.</p>
<p>For the parser and unparser, replacement is currently the only supported form of customization.
More information on these interfaces can be found in the following documentation sections:</p>
<ul>
<li><a href="/Documentation/Under_the_Hood/FreTool_Interfaces#frescoper-3">FreScoper</a></li>
<li><a href="/Documentation/Under_the_Hood/FreTool_Interfaces#fretyper-5">FreTyper</a></li>
<li><a
  href="/Documentation/Under_the_Hood/FreTool_Interfaces#frevalidator-4"
>FreValidator</a></li>
<li><a href="/Documentation/Under_the_Hood/FreTool_Interfaces#frereader-6">FreReader</a></li>
<li><a href="/Documentation/Under_the_Hood/FreTool_Interfaces#frewriter-7">FreWriter</a></li>
<li><a href="/Documentation/Under_the_Hood/FreTool_Interfaces#frestdlib-8">FreStdlib</a></li>
</ul>


<PrevNextSection {prevLink} {nextLink} />